"use client";

import { useState, useRef, useEffect } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/lib/hooks/useAuth";
import { Button } from "@/components/ui/Button";
import VocalAnalysisReport from "@/components/ai/VocalAnalysisReport";
import type { VocalAnalysisResult } from "@/components/ai/VocalAnalysisReport";
import AnalysisTypeSelector from "@/components/ai/AnalysisTypeSelector";
import type {
  AnalysisType,
  AnalysisTypeDetection,
} from "@/components/ai/AnalysisTypeSelector";
import { detectAnalysisType } from "@/lib/api/detect-analysis-type";
import { analyzeByType } from "@/lib/api/analysis-pipeline";
import { getPointBalance, spendPoints } from "@/lib/api/points";
import { saveAnalysisResult } from "@/lib/api/analysis-save";
import { Upload, Play, Pause, Loader2, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { TonyAvatar } from "@/components/ai/TonyAvatar";
import {
  createIOSCompatibleMediaRecorder,
  getFileExtensionFromMimeType,
} from "@/lib/utils/media-recorder-ios";
import { getMicrophoneStream } from "@/lib/utils/getMicrophoneStream";
import { MIN_ANALYSIS_AUDIO_SECONDS } from "@/lib/utils/audio-usability";

const ADVANCED_ANALYSIS_MIN_DURATION = 5000;
const FREE_ANALYSIS_MIN_DURATION = 1200;

const ADVANCED_LOADER_PHASES = [
  {
    title: "내 목소리의 '왜'를 찾는 중...",
    description: "음정·리듬·에너지·톤 패턴을 먼저 읽고 있어요.",
  },
  {
    title: "레퍼런스와 비교해 원인을 좁히는 중...",
    description: "유사 아티스트 메타데이터로 흐름을 맞춰봅니다.",
  },
  {
    title: "전문가 코칭으로 다음 단계를 만들어요",
    description: "바로 실천 가능한 개선 포인트를 정리합니다.",
  },
];

const QUICK_LOADER_PHASES = [
  {
    title: "핵심 지표를 빠르게 읽는 중...",
    description: "가장 약한 영역부터 먼저 진단할게요.",
  },
  {
    title: "리포트를 정리하는 중...",
    description: "한 줄 요약 + 스냅샷 형태로 보여드려요.",
  },
];

const getMinAnalysisDuration = (tier: "free" | "advanced") =>
  tier === "advanced"
    ? ADVANCED_ANALYSIS_MIN_DURATION
    : FREE_ANALYSIS_MIN_DURATION;

export default function MusicAnalysisAnalyzePage() {
  const router = useRouter();
  const { user, loading } = useAuth();

  const [selectedTier, setSelectedTier] = useState<"free" | "advanced">("free");
  const [pointsAlreadyDeducted, setPointsAlreadyDeducted] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingElapsedSeconds, setRecordingElapsedSeconds] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] =
    useState<VocalAnalysisResult | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [showAnalysisLoader, setShowAnalysisLoader] = useState(false);
  const [loaderPhase, setLoaderPhase] = useState(0);
  const [activeLoaderPhases, setActiveLoaderPhases] = useState(
    ADVANCED_LOADER_PHASES
  );
  const [loaderIntervalMs, setLoaderIntervalMs] = useState(1600);
  const [detectedType, setDetectedType] =
    useState<AnalysisTypeDetection | null>(null);
  const [selectedAnalysisType, setSelectedAnalysisType] =
    useState<AnalysisType>("vocal");
  const [isDetecting, setIsDetecting] = useState(false);

  // 토니 채팅 상태
  const [chatMessages, setChatMessages] = useState<
    Array<{ role: "user" | "tony"; message: string }>
  >([]);
  const [chatInput, setChatInput] = useState("");
  const [isChatting, setIsChatting] = useState(false);
  const [chatCount, setChatCount] = useState(0);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingStreamRef = useRef<MediaStream | null>(null);
  const recordingStartedAtRef = useRef<number | null>(null);
  const recordingTimerRef = useRef<number | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  const createAnalysisId = () => {
    try {
      if (
        typeof crypto !== "undefined" &&
        typeof crypto.randomUUID === "function"
      ) {
        return `analysis_${crypto.randomUUID()}`;
      }
    } catch {}
    return `analysis_${Date.now()}`;
  };

  useEffect(() => {
    return () => {
      if (recordingTimerRef.current !== null) {
        window.clearInterval(recordingTimerRef.current);
      }
      recordingStreamRef.current?.getTracks().forEach(track => track.stop());
    };
  }, []);

  useEffect(() => {
    if (!showAnalysisLoader) {
      setLoaderPhase(0);
      return;
    }

    const phaseCount = activeLoaderPhases.length;
    if (phaseCount === 0) {
      return;
    }

    setLoaderPhase(0);
    const intervalId = window.setInterval(() => {
      setLoaderPhase(prev => {
        if (prev >= phaseCount - 1) {
          return prev;
        }
        return prev + 1;
      });
    }, loaderIntervalMs);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [showAnalysisLoader, activeLoaderPhases, loaderIntervalMs]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const handleAdvancedUpgrade = async () => {
    if (!user) {
      toast.error("로그인이 필요합니다. 회원가입 후 무료로 1회 체험하세요!", {
        duration: 5000,
      });
      router.push(
        `/auth/signup?callbackUrl=${encodeURIComponent("/music-analysis/analyze")}`
      );
      return;
    }

    try {
      const analysisId = createAnalysisId();
      const balance = await getPointBalance(user.uid);

      if (balance < 2900) {
        toast.error(
          `포인트가 부족합니다. (현재: ${balance}P, 필요: 2,900P) 회원가입 시 5,000P 지급!`,
          {
            duration: 5000,
          }
        );
        return;
      }

      const deductResult = await spendPoints(
        user.uid,
        2900,
        "AI 심화 분석",
        analysisId
      );

      if (!deductResult.success) {
        toast.error(deductResult.error || "포인트 차감에 실패했습니다.");
        return;
      }

      toast.success(`포인트 차감 완료! (잔액: ${deductResult.newBalance}P)`, {
        icon: "",
      });

      setSelectedTier("advanced");
      setPointsAlreadyDeducted(true);

      await handleMusicAnalyze({
        tierOverride: "advanced",
        skipPointDeduction: true,
        analysisIdOverride: analysisId,
      });
    } catch (error) {
      console.error("포인트 처리 오류:", error);
      toast.error("포인트 처리 중 오류가 발생했습니다.");
    }
  };

  const validateAndSetFile = async (file: File) => {
    const validTypes = [
      "audio/wav",
      "audio/mpeg",
      "audio/mp3",
      "audio/ogg",
      "audio/webm",
      "audio/mp4",
      "audio/m4a",
      "audio/aac",
      "audio/x-m4a",
    ];
    if (
      !validTypes.includes(file.type) &&
      !file.name.match(/\.(wav|mp3|ogg|webm|m4a|aac|mp4)$/i)
    ) {
      toast.error(
        "지원되지 않는 파일 형식입니다. WAV, MP3, OGG, WEBM, M4A 파일만 업로드 가능합니다."
      );
      return;
    }

    const maxSize = 30 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("파일 크기가 너무 큽니다. 최대 30MB까지 업로드 가능합니다.");
      return;
    }

    const audio = new Audio();
    audio.onloadedmetadata = () => {
      if (audio.duration > 300) {
        toast.error("오디오 길이가 너무 깁니다. 최대 5분까지 분석 가능합니다.");
        return;
      }
    };
    audio.src = URL.createObjectURL(file);

    const url = URL.createObjectURL(file);

    setSelectedFile(file);
    setAudioUrl(url);
    setAnalysisResult(null);
    setPointsAlreadyDeducted(false);

    setIsDetecting(true);
    try {
      const detection = await detectAnalysisType(file);
      setDetectedType(detection);
      setSelectedAnalysisType(detection.type);
    } catch (error) {
      console.error("분석 유형 감지 실패:", error);
      setDetectedType(null);
    } finally {
      setIsDetecting(false);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const startRecording = async () => {
    try {
      // 이전 스트림이 남아 있으면 해제
      recordingStreamRef.current?.getTracks().forEach(track => track.stop());
      recordingStreamRef.current = null;

      const stream = await getMicrophoneStream();
      recordingStreamRef.current = stream;

      const mediaRecorder = createIOSCompatibleMediaRecorder(stream);
      const mimeType = mediaRecorder.mimeType || "audio/webm";
      const extension = getFileExtensionFromMimeType(mimeType);

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = event => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: mimeType,
        });

        const audioFile = new File(
          [audioBlob],
          `recording_${Date.now()}.${extension}`,
          {
            type: mimeType,
          }
        );

        setSelectedFile(audioFile);
        setAudioUrl(URL.createObjectURL(audioBlob));
        setAnalysisResult(null);
        toast.success("녹음이 완료되었습니다!");

        recordingStreamRef.current?.getTracks().forEach(track => track.stop());
        recordingStreamRef.current = null;
        recordingStartedAtRef.current = null;
        if (recordingTimerRef.current !== null) {
          window.clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }
        setRecordingElapsedSeconds(0);
      };

      recordingStartedAtRef.current = Date.now();
      setRecordingElapsedSeconds(0);
      recordingTimerRef.current = window.setInterval(() => {
        if (recordingStartedAtRef.current) {
          setRecordingElapsedSeconds(
            Math.floor((Date.now() - recordingStartedAtRef.current) / 1000)
          );
        }
      }, 250);

      mediaRecorder.start(250);
      setIsRecording(true);
    } catch (error) {
      console.error("녹음 시작 실패:", error);
      toast.error(
        error instanceof Error ? error.message : "마이크 권한을 허용해주세요."
      );
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      const elapsedSeconds = recordingStartedAtRef.current
        ? Math.floor((Date.now() - recordingStartedAtRef.current) / 1000)
        : recordingElapsedSeconds;

      if (elapsedSeconds < MIN_ANALYSIS_AUDIO_SECONDS) {
        toast(
          `아직 ${elapsedSeconds}초예요. 분석하려면 ${MIN_ANALYSIS_AUDIO_SECONDS}초 이상 녹음하는 게 좋아요.`,
          { icon: "🎙️", duration: 3500 }
        );
      }

      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const togglePlay = async () => {
    if (!audioRef.current) {
      return;
    }

    try {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        await audioRef.current.play();
        setIsPlaying(true);
      }
    } catch (error) {
      console.error("오디오 재생 실패:", error);
      toast.error("오디오 재생에 실패했습니다.");
      setIsPlaying(false);
    }
  };

  const handleMusicAnalyze = async (options?: {
    tierOverride?: "free" | "advanced";
    skipPointDeduction?: boolean;
    analysisIdOverride?: string;
  }) => {
    const effectiveTier = options?.tierOverride ?? selectedTier;
    const skipPointDeduction = options?.skipPointDeduction ?? false;
    const analysisId = options?.analysisIdOverride || createAnalysisId();

    if (!selectedFile) {
      toast.error("분석할 오디오 파일을 선택해주세요.");
      return;
    }

    setIsAnalyzing(true);
    setAnalysisResult(null);

    try {
      if (
        effectiveTier === "advanced" &&
        !skipPointDeduction &&
        !pointsAlreadyDeducted
      ) {
        if (!user) {
          toast.error(
            "로그인이 필요합니다. 회원가입 후 무료로 1회 체험하세요!",
            {
              duration: 5000,
            }
          );
          setIsAnalyzing(false);
          return;
        }

        const balance = await getPointBalance(user.uid);

        if (balance < 2900) {
          toast.error(
            `포인트가 부족합니다. (현재: ${balance}P, 필요: 2,900P) 회원가입 시 5,000P 지급!`,
            {
              duration: 5000,
            }
          );
          setIsAnalyzing(false);
          return;
        }

        const deductResult = await spendPoints(
          user.uid,
          2900,
          "AI 심화 분석",
          analysisId
        );

        if (!deductResult.success) {
          toast.error(deductResult.error || "포인트 차감에 실패했습니다.");
          setIsAnalyzing(false);
          return;
        }

        toast(`포인트 차감 완료! (잔액: ${deductResult.newBalance}P)`, {
          icon: "",
        });
        setPointsAlreadyDeducted(true);
      }

      const phases =
        effectiveTier === "advanced"
          ? ADVANCED_LOADER_PHASES
          : QUICK_LOADER_PHASES;
      setActiveLoaderPhases(phases);

      const minDuration = getMinAnalysisDuration(effectiveTier);
      const interval = Math.max(
        Math.floor(minDuration / Math.max(phases.length, 1)),
        450
      );
      setLoaderIntervalMs(interval);
      setShowAnalysisLoader(true);

      const analysisStartedAt = Date.now();

      const result = await analyzeByType(selectedFile, selectedAnalysisType, {
        useSpotify: effectiveTier === "advanced",
      });

      const revealResult = async () => {
        setAnalysisResult(result);

        if (user) {
          try {
            const saveResult = await saveAnalysisResult(
              analysisId,
              user.uid,
              selectedFile.name,
              selectedAnalysisType,
              effectiveTier,
              result,
              audioUrl || undefined
            );

            if (saveResult.success) {
              toast.success("분석 결과가 저장되었습니다!", {
                icon: "",
                duration: 3000,
              });
            } else {
              console.error("분석 결과 저장 실패:", saveResult.error);
              toast.error(
                saveResult.error ||
                  "분석 결과 저장에 실패했습니다. 잠시 후 다시 시도해주세요."
              );
            }
          } catch (error) {
            console.error("분석 결과 저장 중 오류:", error);
            toast.error(
              error instanceof Error
                ? error.message
                : "분석 결과 저장 중 오류가 발생했습니다."
            );
          }
        }

        setShowAnalysisLoader(false);
        setIsAnalyzing(false);

        setTimeout(() => {
          resultsRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 400);
      };

      const elapsed = Date.now() - analysisStartedAt;
      const remaining = Math.max(
        0,
        getMinAnalysisDuration(effectiveTier) - elapsed
      );

      if (remaining > 0) {
        window.setTimeout(() => {
          void revealResult();
        }, remaining);
      } else {
        void revealResult();
      }
    } catch (error) {
      console.error("음악 분석 오류:", error);
      toast.error(
        error instanceof Error ? error.message : "분석 중 오류가 발생했습니다."
      );
      setShowAnalysisLoader(false);
      setIsAnalyzing(false);
    }
  };

  const hasLoaderPhases = activeLoaderPhases.length > 0;
  const safeLoaderIndex = hasLoaderPhases
    ? Math.min(loaderPhase, activeLoaderPhases.length - 1)
    : 0;
  const currentLoaderPhase = hasLoaderPhases
    ? activeLoaderPhases[safeLoaderIndex]
    : {
        title: "AI가 분석 중입니다...",
        description: "결과를 준비하고 있으니 잠시만 기다려주세요.",
      };
  const loaderProgress = hasLoaderPhases
    ? ((safeLoaderIndex + 1) / activeLoaderPhases.length) * 100
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-darkMain to-secondary">
      {showAnalysisLoader && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/75 backdrop-blur-base">
          <div className="w-[90%] max-w-lg rounded-3xl bg-white/95 p-10 shadow-[0_40px_80px_rgba(79,70,229,0.25)] text-center relative overflow-hidden">
            <div className="absolute inset-x-0 -top-28 h-48 bg-gradient-to-br from-teal-200/70 via-purple-100/60 to-transparent blur-3xl" />
            <div className="relative flex flex-col items-center gap-6">
              <div className="relative flex h-28 w-28 items-center justify-center">
                <div className="absolute inset-0 rounded-full border-2 border-purple-200/60 animate-[pulseRing_2.6s_ease-out_infinite]" />
                <div className="absolute inset-3 rounded-full border border-purple-300/70 animate-[pulseRing_2.6s_ease-out_infinite] [animation-delay:0.8s]" />
                <div className="relative flex h-16 w-full items-end justify-center gap-[6px]">
                  {Array.from({ length: 9 }).map((_, index) => (
                    <span
                      key={index}
                      className="w-2.5 sm:w-3 rounded-full bg-gradient-to-t from-teal-500 via-purple-400 to-cyan-300"
                      style={{
                        animation: `analysisWave 1.6s ease-in-out ${index * 0.12}s infinite`,
                        height: `${40 + (index % 5) * 12}%`,
                      }}
                    />
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-lg sm:text-xl font-semibold text-gray-900">
                  {currentLoaderPhase.title}
                </p>
                <p className="text-base sm:text-base text-gray-600 leading-relaxed">
                  {currentLoaderPhase.description}
                </p>
              </div>

              <div className="w-full h-2.5 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-teal-500 via-purple-400 to-purple-300 transition-all duration-300"
                  style={{ width: `${Math.min(100, loaderProgress)}%` }}
                />
              </div>

              <p className="text-[11px] uppercase tracking-[0.4em] text-gray-400">
                analyzing
              </p>
            </div>

            <style jsx>{`
              @keyframes analysisWave {
                0%,
                100% {
                  transform: scaleY(0.3);
                  opacity: 0.45;
                }
                50% {
                  transform: scaleY(1);
                  opacity: 1;
                }
              }

              @keyframes pulseRing {
                0% {
                  transform: scale(0.65);
                  opacity: 0.7;
                }
                70% {
                  transform: scale(1.2);
                  opacity: 0;
                }
                100% {
                  opacity: 0;
                  transform: scale(1.2);
                }
              }
            `}</style>
          </div>
        </div>
      )}

      {/* Hero + Upload 합친 섹션 */}
      <section
        className="relative pt-20 sm:pt-24 md:pt-28 lg:pt-32 pb-12 sm:pb-16 md:pb-20 lg:pb-24 overflow-hidden"
        id="upload-section"
      >
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            {/* 왼쪽: 텍스트 존 */}
            <div className="space-y-6">
              {/* 배지 */}
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-base rounded-full text-base text-purple-700 font-semibold shadow-md border border-purple-200"
              >
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                <span>
                  지금까지 <strong className="text-purple-900">1,200명+</strong>
                  이 AI 음악 분석을 경험했습니다
                </span>
              </motion.div>

              {/* 타이틀 */}
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-5xl sm:text-6xl md:text-7xl font-extrabold text-white leading-tight text-left"
              >
                <span className="block mb-2">내 음악을</span>
                <span className="block bg-gradient-to-r from-point via-point2 to-point3 bg-clip-text text-transparent">
                  AI가 분석합니다
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-base sm:text-lg md:text-xl text-white/90 font-medium text-left"
              >
                객관적이고 정확한 음악 분석을
                <br />
                <span className="text-point3 font-semibold">5초 만에</span>{" "}
                받아보세요
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-sm sm:text-base text-white/70 text-left"
              >
                AI가 당신의 음악을 처음부터 끝까지 듣고,
                <br />
                전문가 수준의 피드백을 제공합니다
              </motion.p>

              {/* 신뢰 지표 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="flex flex-wrap gap-3 mt-6"
              >
                <div className="flex items-center gap-2 bg-white/90 backdrop-blur-base px-4 py-2 rounded-full shadow-base border border-purple-200">
                  <span className="text-base text-gray-700">
                    <strong className="text-purple-600">즉시</strong> 결과 확인
                  </span>
                </div>
                <div className="flex items-center gap-2 bg-white/90 backdrop-blur-base px-4 py-2 rounded-full shadow-base border border-indigo-200">
                  <span className="text-base text-gray-700">
                    <strong className="text-indigo-600">내 음악</strong> DNA
                  </span>
                </div>
                <div className="flex items-center gap-2 bg-white/90 backdrop-blur-base px-4 py-2 rounded-full shadow-base border border-purple-200">
                  <span className="text-base text-gray-700">
                    <strong className="text-purple-600">AI 토니의</strong>{" "}
                    코멘트
                  </span>
                </div>
              </motion.div>
            </div>

            {/* 오른쪽: 업로드 존 */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8 lg:p-10 border border-gray-100"
            >
              <div className="mb-6">
                <h3 className="text-2xl sm:text-3xl font-bold mb-4 text-gray-900 text-left">
                  음악 파일 업로드
                </h3>
                <p className="text-base sm:text-lg text-gray-600 mb-2 text-left">
                  WAV, MP3, OGG, WEBM (최대 30MB)을 업로드하거나, 직접 녹음할 수
                  있습니다
                </p>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 rounded-full mt-4">
                  <span className="text-base sm:text-base text-purple-700 font-medium">
                    업로드 즉시 5초 내 분석 결과 확인
                  </span>
                </div>
              </div>

              {/* 드래그 앤 드롭 */}
              <div
                className={`relative border-2 border-dashed rounded-2xl p-8 text-center transition-all duration-300 ${
                  dragActive
                    ? "border-purple-500 bg-purple-50 scale-105"
                    : "border-gray-300 hover:border-purple-400 hover:bg-purple-50/50"
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-teal-50/50 to-purple-50/50 rounded-2xl opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-teal-100 to-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Upload className="w-8 h-8 sm:w-10 sm:h-10 text-teal-600" />
                  </div>
                  <p className="text-base sm:text-lg text-gray-700 font-semibold mb-2">
                    파일을 드래그하거나 클릭하여 업로드
                  </p>
                  <p className="text-base text-gray-500 mb-4">
                    또는 아래 버튼을 클릭하여 파일 선택
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="audio/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-teal-500 to-purple-400 hover:from-teal-600 hover:to-purple-500 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                  >
                    <Upload className="w-5 h-5 mr-2" />
                    파일 선택
                  </Button>
                </div>
              </div>

              {/* 또는 */}
              <div className="flex items-center my-6">
                <div className="flex-1 border-t border-gray-300"></div>
                <span className="px-4 text-gray-500 text-base">또는</span>
                <div className="flex-1 border-t border-gray-300"></div>
              </div>

              {/* 녹음 */}
              <div className="flex justify-center">
                {!isRecording ? (
                  <Button
                    onClick={startRecording}
                    className="bg-gradient-to-r from-purple-500 via-pink-400 to-purple-500 hover:from-purple-600 hover:via-pink-500 hover:to-purple-600 text-white font-medium transition px-6 py-3 rounded-xl shadow-lg hover:shadow-xl inline-flex items-center gap-3"
                    disabled={isRecording}
                  >
                    <TonyAvatar size="sm" />
                    <span>토니와 함께 녹음하기</span>
                  </Button>
                ) : (
                  <Button
                    onClick={stopRecording}
                    className="bg-gray-600 hover:bg-gray-700 animate-pulse text-white font-medium px-6 py-3 rounded-xl inline-flex items-center gap-3"
                  >
                    <div className="w-4 h-4 bg-red-500 rounded-full animate-ping" />
                    <span>
                      녹음 중지 ({recordingElapsedSeconds}초 / 최소{" "}
                      {MIN_ANALYSIS_AUDIO_SECONDS}초)
                    </span>
                  </Button>
                )}
              </div>

              {/* 선택된 파일 */}
              {selectedFile && audioUrl && (
                <div className="mt-8 space-y-6">
                  <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                    <div className="flex items-center gap-4 mb-4">
                      <button
                        onClick={togglePlay}
                        className="w-12 h-12 rounded-full bg-gradient-to-r from-teal-500 to-purple-400 flex items-center justify-center transition hover:opacity-80"
                      >
                        {isPlaying ? (
                          <Pause className="w-6 h-6 text-white" />
                        ) : (
                          <Play className="w-6 h-6 text-white ml-0.5" />
                        )}
                      </button>
                      <div className="flex-1">
                        <p className="text-base font-medium text-gray-900 mb-1">
                          {selectedFile.name}
                        </p>
                        <p className="text-base text-gray-500">
                          {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                    <audio
                      ref={audioRef}
                      src={audioUrl}
                      onEnded={() => setIsPlaying(false)}
                    />
                  </div>

                  {/* 분석 유형 */}
                  <div className="mb-8">
                    {isDetecting ? (
                      <div className="text-center py-8">
                        <div className="w-16 h-16 bg-gradient-to-r from-teal-500 to-purple-400 rounded-2xl flex items-center justify-center mx-auto mb-4">
                          <Loader2 className="w-8 h-8 text-white animate-spin" />
                        </div>
                        <h4 className="text-xl font-bold text-gray-900 mb-2">
                          AI가 자동으로 분석 유형을 감지하고 있습니다
                        </h4>
                        <p className="text-gray-600">잠시만 기다려주세요...</p>
                        <div className="mt-4 w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-gradient-to-r from-teal-500 to-purple-400 h-2 rounded-full animate-pulse"
                            style={{ width: "60%" }}
                          ></div>
                        </div>
                      </div>
                    ) : detectedType ? (
                      <div className="text-center py-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl border-2 border-green-200 mb-6">
                        <h4 className="text-lg font-bold text-gray-900 mb-1">
                          AI가 분석 유형을 감지했습니다
                        </h4>
                        <p className="text-gray-600">
                          {detectedType.type === "vocal"
                            ? "보컬"
                            : detectedType.type === "instrument"
                              ? "연주"
                              : "작곡"}{" "}
                          분석으로 감지되었습니다.
                          <br />
                          <span className="text-base text-gray-500">
                            (신뢰도: {Math.round(detectedType.confidence * 100)}
                            %)
                          </span>
                        </p>
                      </div>
                    ) : null}

                    <AnalysisTypeSelector
                      detectedType={detectedType || undefined}
                      selectedType={selectedAnalysisType}
                      onTypeChange={setSelectedAnalysisType}
                      isLoading={isDetecting || isAnalyzing}
                    />
                  </div>

                  {/* 분석 버튼 */}
                  <Button
                    onClick={() => handleMusicAnalyze()}
                    disabled={isAnalyzing || isDetecting}
                    className="w-full py-3 sm:py-4 bg-gradient-to-r from-teal-600 to-purple-600 hover:from-teal-700 hover:to-purple-700 text-white font-bold rounded-xl text-base sm:text-base md:text-lg"
                  >
                    {isAnalyzing ? (
                      <>
                        <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 mr-2 animate-spin" />
                        <span className="hidden sm:inline">
                          미니 분석 중... (5초 내 완료)
                        </span>
                        <span className="sm:hidden">분석 중...</span>
                      </>
                    ) : isDetecting ? (
                      <>
                        <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 mr-2 animate-spin" />
                        분석 유형 감지 중...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                        <span className="hidden sm:inline">
                          즉시 분석 시작하기 (무료)
                        </span>
                        <span className="sm:hidden">분석 시작하기</span>
                      </>
                    )}
                  </Button>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* 분석 결과 */}
      {analysisResult && (
        <section
          ref={resultsRef}
          className="pt-6 pb-8 sm:pb-12 md:pb-16 lg:pb-20"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 sm:mb-10 md:mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">
                AI 분석 결과
              </h2>
              <p className="text-base sm:text-base md:text-lg text-white/80 px-2">
                AI가 당신의 음악을 전문적으로 분석했습니다
              </p>
            </div>

            <VocalAnalysisReport
              result={analysisResult}
              tier={selectedTier}
              audioUrl={audioUrl}
              isPlaying={isPlaying}
              chatMessages={chatMessages}
              setChatMessages={setChatMessages}
              chatInput={chatInput}
              setChatInput={setChatInput}
              isChatting={isChatting}
              setIsChatting={setIsChatting}
              user={user}
              chatCount={chatCount}
              setChatCount={setChatCount}
              router={router}
              onAdvancedAnalysis={handleAdvancedUpgrade}
            />

          </div>
        </section>
      )}
    </div>
  );
}
